import { createSelector } from '@ngrx/store';
import { MemoizedSelector } from '@ngrx/store/src/selector';
import { PortfolioSmartSearch } from '@platform/smart-search/models/smart-search.model';
import { AppState } from '@shared/models/app-state.model';
import { selectPropertyMortgageSelectItems } from '@shared/store/common.selectors';
import { selectCanAccessSmartSearch } from '@shared/store/company/company.selectors';
import { selectIsLoadingSmartSearchCases, selectSmartSearchCases } from '@shared/store/smart-search-cases/smart-search-cases.selectors';
import {
  selectIsLoadingSmartSearchDeals,
  selectSmartSearchDealEntities,
} from '@shared/store/smart-search-deals/smart-search-deals.selectors';
import { selectIsLoadingSmartSearches, selectSmartSearches } from '@shared/store/smart-searches/smart-searches.selectors';
import { portfolioSmartSearchAdapter } from './adapters/portfolio-smart-search.adapter';

export const selectIsLoadingPortfolioSmartSearch = createSelector(
  selectIsLoadingSmartSearches,
  selectIsLoadingSmartSearchCases,
  selectIsLoadingSmartSearchDeals,
  (isLoadingSmartSearches, isLoadingSmartSearchCases, isLoadingSmartSearchDeals: boolean): boolean =>
    isLoadingSmartSearches || isLoadingSmartSearchCases || isLoadingSmartSearchDeals,
);

export const selectPortfolioSmartSearches = createSelector(
  selectIsLoadingPortfolioSmartSearch,
  selectSmartSearches,
  selectSmartSearchCases,
  selectSmartSearchDealEntities,
  selectPropertyMortgageSelectItems,
  selectCanAccessSmartSearch,
  (
    isLoadingDashboardSmartSearchWithCaseAndDeals,
    smartSearches,
    smartSearchCases,
    smartSearchDealEntities,
    propertyMortgageSelectItems,
    canAccessSmartSearch,
  ): PortfolioSmartSearch[] => {
    if (isLoadingDashboardSmartSearchWithCaseAndDeals || !canAccessSmartSearch) return [];

    return smartSearches.map((smartSearch) =>
      portfolioSmartSearchAdapter(smartSearch, smartSearchDealEntities, smartSearchCases, propertyMortgageSelectItems),
    );
  },
);

export const selectPortfolioSmartSearch = (smartSearchId: string): MemoizedSelector<AppState, PortfolioSmartSearch> =>
  createSelector(selectPortfolioSmartSearches, (smartSearches): PortfolioSmartSearch => smartSearches.find((s) => s.id === smartSearchId));

export const selectPortfolioSmartSearchesWithoutProperties = createSelector(
  selectPortfolioSmartSearches,
  (smartSearches): PortfolioSmartSearch[] => {
    return smartSearches.filter((s) => !s.forRemortgageBorrowMore?.propertyId && !s.forMortgageProduct?.propertyId);
  },
);
